// src/components/leaderboard/TopPerformers.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { UserStats } from '@/types';
import { Crown } from 'lucide-react';

interface TopPerformersProps {
  topThree: UserStats[];
}

export const TopPerformers: React.FC<TopPerformersProps> = ({ topThree }) => {
  if (topThree.length === 0) return null;

  const podiumOrder = [topThree[1], topThree[0], topThree[2]].filter(Boolean);
  const heights = ['h-32', 'h-40', 'h-28'];

  return (
    <Card className="overflow-hidden">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Crown className="w-5 h-5 text-yellow-500" />
          Top Performers
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-end justify-center gap-4 pb-4">
          {podiumOrder.map((user, index) => {
            if (!user) return null;
            const actualRank = index === 1 ? 1 : index === 0 ? 2 : 3;
            const colors = [
              'from-gray-400 to-gray-500',
              'from-yellow-400 to-yellow-600',
              'from-amber-600 to-orange-700',
            ];

            return (
              <motion.div
                key={user.userId}
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                className="flex flex-col items-center"
              >
                <Avatar className={`h-16 w-16 mb-3 ring-4 ring-${actualRank === 1 ? 'yellow' : actualRank === 2 ? 'gray' : 'amber'}-500`}>
                  <AvatarImage src={user.userPhoto} alt={user.userName} />
                  <AvatarFallback>{user.userName.charAt(0)}</AvatarFallback>
                </Avatar>
                <div
                  className={`${heights[index]} w-24 rounded-t-lg bg-gradient-to-t ${colors[index]} flex flex-col items-center justify-start pt-4 shadow-lg`}
                >
                  <div className="text-3xl mb-2">
                    {actualRank === 1 ? '👑' : actualRank === 2 ? '🥈' : '🥉'}
                  </div>
                  <div className="text-sm font-bold text-white text-center px-2 truncate w-full">
                    {user.userName.split(' ')[0]}
                  </div>
                  <div className="text-2xl font-bold text-white mt-1">
                    {user.currentStreak}
                  </div>
                  <div className="text-xs text-white/80">days</div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

